import styled from 'styled-components';
import { Panel, Button, IconButton, Switch } from '@/ui';
import { usePreferences } from '@/hooks/usePreferences';
import {
  ACCOUNT_MENU_LABELS,
  DEFAULT_ACCOUNT_MENU,
  normalizeAccountMenu,
  type AccountMenuShortcut,
} from '@/core/models/accountMenu';

const Card = styled(Panel)`
  margin-top: 24px;
  h3 {
    margin: 0 0 8px;
  }
  p {
    color: ${({ theme }) => theme.color.textMuted};
    font-size: ${({ theme }) => theme.fontSize.sm};
  }
  ol {
    list-style: none;
    margin: 16px 0;
    padding: 0;
  }
  li {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 8px 0;
  }
  li > span {
    flex: 1;
    min-width: 100px;
  }
`;
const MoveUp = styled(IconButton)`
  svg {
    transform: rotate(180deg);
  }
`;

export function AccountMenuSettings() {
  const { prefs, setPreference } = usePreferences();
  const visible = normalizeAccountMenu(prefs.accountMenuShortcuts);
  const all = [...visible, ...DEFAULT_ACCOUNT_MENU.filter((key) => !visible.includes(key))];
  function move(key: AccountMenuShortcut, delta: number) {
    const index = visible.indexOf(key);
    const next = [...visible];
    const swap = next[index + delta];
    if (!swap) return;
    next[index] = swap;
    next[index + delta] = key;
    void setPreference('accountMenuShortcuts', next);
  }
  return (
    <Card as="section" aria-label="Account menu customization">
      <h3>Account menu</h3>
      <p>
        Choose the shortcuts in your bottom-left profile menu and put them in the order you prefer.
        Saved for this account on this browser.
      </p>
      <p>Visit Profile, Settings, and Sign out are always available.</p>
      <ol>
        {all.map((key) => (
          <li key={key}>
            <span>{ACCOUNT_MENU_LABELS[key]}</span>
            <Switch
              label={`Show ${ACCOUNT_MENU_LABELS[key]}`}
              checked={visible.includes(key)}
              onChange={(show) =>
                void setPreference(
                  'accountMenuShortcuts',
                  show ? [...visible, key] : visible.filter((item) => item !== key),
                )
              }
            />
            <MoveUp
              icon="chevron-down"
              label={`Move ${ACCOUNT_MENU_LABELS[key]} up`}
              disabled={visible.indexOf(key) <= 0}
              onClick={() => move(key, -1)}
            />
            <IconButton
              icon="chevron-down"
              label={`Move ${ACCOUNT_MENU_LABELS[key]} down`}
              disabled={!visible.includes(key) || visible.indexOf(key) === visible.length - 1}
              onClick={() => move(key, 1)}
            />
          </li>
        ))}
      </ol>
      <Button
        variant="ghost"
        onClick={() => void setPreference('accountMenuShortcuts', [...DEFAULT_ACCOUNT_MENU])}
      >
        Restore account menu defaults
      </Button>
    </Card>
  );
}
